import React, { useState, useEffect } from 'react';
import { X, Calendar, Save } from 'lucide-react';
import { activityStorage } from '../../services/activityStorage';
import { persistedEventRecords } from '../../services/mockData/events';
import { Activity, RecordType } from '../../types/activity';
import type { Event, EventStatus } from '../../types/event';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { TIMEZONE_OPTIONS, detectLocalTimezone } from '../common/TimeWithLocalHover';
import { useToast } from '../../contexts/ToastContext';

interface LogMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  recordId: string;
  recordType: RecordType;
  contactName?: string;
  relatedToName?: string;
  onMeetingLogged?: () => void;
  /** Pass an existing activity to enable edit mode */
  editingActivity?: Activity | null;
}

type MeetingType = 'in-person' | 'video-call' | 'phone-call';
type MeetingOutcome = 'scheduled' | 'completed' | 'cancelled' | 'no-show';

const meetingTypes: { value: MeetingType; label: string }[] = [
  { value: 'in-person', label: 'In-Person' },
  { value: 'video-call', label: 'Video Call' },
  { value: 'phone-call', label: 'Phone Call' },
];

const meetingOutcomes: { value: MeetingOutcome; label: string }[] = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no-show', label: 'No Show' },
];

// Map our outcomes to Event status
const outcomeToEventStatus: Record<MeetingOutcome, EventStatus> = {
  'scheduled': 'Scheduled',
  'completed': 'Completed',
  'cancelled': 'Cancelled',
  'no-show': 'No Show',
};

// Map recordType to Event relatedToType
const recordTypeToRelatedType: Record<string, Event['relatedToType']> = {
  'contact': 'Contact',
  'opportunity': 'Opportunity',
  'case': 'Case',
  'client': 'Account',
  'firm': 'Account',
  'account': 'Account',
};

export const LogMeetingModal: React.FC<LogMeetingModalProps> = ({
  isOpen,
  onClose,
  recordId,
  recordType,
  contactName,
  relatedToName,
  onMeetingLogged,
  editingActivity,
}) => {
  const { userId, userName } = useCurrentUser();
  const { success } = useToast();
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('09:00');
  const [duration, setDuration] = useState('30');
  const [meetingType, setMeetingType] = useState<MeetingType>('video-call');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState(contactName || '');
  const [outcome, setOutcome] = useState<MeetingOutcome>('completed');
  const [notes, setNotes] = useState('');
  const [createEvent, setCreateEvent] = useState(true);
  const [timezone, setTimezone] = useState(detectLocalTimezone);
  const [error, setError] = useState('');

  const isEditMode = !!editingActivity;

  // Populate form when editing an existing activity
  useEffect(() => {
    if (editingActivity && isOpen) {
      setTitle(editingActivity.subject || '');
      setDuration(editingActivity.duration?.toString() || '30');
      setLocation(editingActivity.location || '');
      setError('');

      // Parse scheduledAt for date/time
      if (editingActivity.scheduledAt) {
        const scheduled = new Date(editingActivity.scheduledAt);
        setMeetingDate(scheduled.toISOString().split('T')[0]);
        setStartTime(scheduled.toTimeString().slice(0, 5));
      }

      // Parse description to extract meeting type and attendees
      const desc = editingActivity.description || '';
      // Try to extract meeting type from description (e.g., "Video Call | ...")
      if (desc.includes('In-Person')) setMeetingType('in-person');
      else if (desc.includes('Phone Call')) setMeetingType('phone-call');
      else setMeetingType('video-call');

      // Extract attendees (after "Attendees: ")
      const attendeesMatch = desc.match(/Attendees:\s*([^\n]+)/);
      if (attendeesMatch) {
        setAttendees(attendeesMatch[1].trim());
      }

      // Extract notes (everything after double newline)
      const notesMatch = desc.split('\n\n');
      if (notesMatch.length > 1) {
        setNotes(notesMatch.slice(1).join('\n\n'));
      } else {
        setNotes('');
      }

      // Don't create event when editing (it may already exist)
      setCreateEvent(false);
    } else if (!editingActivity && isOpen) {
      // Reset to defaults for new meeting
      setTitle('');
      setMeetingDate(new Date().toISOString().split('T')[0]);
      setStartTime('09:00');
      setDuration('30');
      setMeetingType('video-call');
      setLocation('');
      setAttendees(contactName || '');
      setOutcome('completed');
      setNotes('');
      setCreateEvent(true);
      setError('');
    }
  }, [editingActivity, isOpen, contactName]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Please enter a meeting title');
      return;
    }

    const durationMins = parseInt(duration, 10) || 30;
    const startDateTime = `${meetingDate}T${startTime}:00`;
    const endDate = new Date(startDateTime);
    endDate.setMinutes(endDate.getMinutes() + durationMins);
    const endDateTime = endDate.toISOString();

    // Get user's timezone (use existing for edit, or use selected timezone for new)
    const userTimezone = editingActivity?.timezone || timezone;

    // Build description string
    const descriptionStr = `${meetingTypes.find(m => m.value === meetingType)?.label || meetingType}${location ? ` | ${location}` : ''}${attendees ? ` | Attendees: ${attendees}` : ''}\n\n${notes}`.trim();

    try {
      if (isEditMode && editingActivity) {
        // UPDATE existing activity (async for Gobodo API)
        await activityStorage.update(editingActivity.id, {
          subject: title,
          description: descriptionStr,
          duration: durationMins,
          scheduledAt: startDateTime,
          timezone: userTimezone,
          location: location || undefined,
        });

        success('Meeting updated', 'Meeting details have been saved');
      } else {
        // CREATE new activity (async for Gobodo API)
        await activityStorage.create({
          type: 'meeting',
          recordId,
          recordType,
          userId,
          userName,
          subject: title,
          description: descriptionStr,
          duration: durationMins,
          scheduledAt: startDateTime,
          timezone: userTimezone,
          location: location || undefined,
          source: 'manual',
        });

        // Create Event record (for Events tab) if checkbox checked - only for new meetings
        if (createEvent) {
          const eventId = `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const now = new Date().toISOString();

          const newEvent: Event = {
            id: eventId,
            subject: title,
            type: 'Meeting',
            status: outcomeToEventStatus[outcome],
            priority: 'Normal',
            startDateTime,
            endDateTime,
            duration: durationMins,
            location: location || undefined,
            isAllDay: false,
            isRecurring: false,
            organizer: userName,
            attendees: attendees ? attendees.split(',').map(a => a.trim()) : undefined,
            relatedToId: recordId,
            relatedToName: relatedToName || contactName,
            relatedToType: recordTypeToRelatedType[recordType],
            createdDate: now,
            lastModifiedDate: now,
          };

          persistedEventRecords.add(newEvent as any);
        }

        success('Meeting logged', 'Meeting has been added to the activity timeline');
      }

      // Reset form only for new meetings (not edit)
      if (!isEditMode) {
        setTitle('');
        setMeetingDate(new Date().toISOString().split('T')[0]);
        setStartTime('09:00');
        setDuration('30');
        setMeetingType('video-call');
        setLocation('');
        setAttendees(contactName || '');
        setOutcome('completed');
        setNotes('');
        setCreateEvent(true);
      }
      setError('');

      onMeetingLogged?.();
      onClose();
    } catch (err) {
      console.error('[LogMeetingModal] Error saving meeting:', err);
      setError('Failed to save meeting. Please try again.');
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEditMode ? 'Edit Meeting' : 'Log Meeting'}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Meeting Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setError(''); }}
                placeholder="e.g., Discovery Meeting"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date & Time Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Duration, Type & Timezone Row */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration (mins)
                </label>
                <input
                  type="number"
                  min="5"
                  step="5"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Meeting Type
                </label>
                <select
                  value={meetingType}
                  onChange={(e) => setMeetingType(e.target.value as MeetingType)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {meetingTypes.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.abbr})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Zoom, Conference Room A, 123 Main St"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Attendees */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Attendees
              </label>
              <input
                type="text"
                value={attendees}
                onChange={(e) => setAttendees(e.target.value)}
                placeholder="e.g., Jennifer Taylor, John Smith"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Outcome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Outcome
              </label>
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as MeetingOutcome)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {meetingOutcomes.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Meeting notes..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Create Event Checkbox - only show for new meetings */}
            {!isEditMode && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createEvent"
                  checked={createEvent}
                  onChange={(e) => setCreateEvent(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="createEvent" className="text-sm text-gray-700 dark:text-gray-300">
                  Also create in Events tab
                </label>
              </div>
            )}

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                {isEditMode ? <Save className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
                {isEditMode ? 'Save Changes' : 'Log Meeting'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
